/**
 * 患者实体 (Patient Entity)
 *
 * 聚合根，包含患者核心业务逻辑。
 * 字段名与 patientStandard.ts 对齐。
 *
 * @module patient-management/domain/entities
 */

import { PatientStatus } from "./PatientStatus";

export type ID = string;
export type Timestamp = string | Date;

/**
 * 患者性别（与后端一致使用中文值）
 *
 * 规范值为 GB/T 2261.1 名称 '男'/'女'/'未知'/'未说明'；
 * 'male'/'female'/'unknown'/'其他' 仅为读取历史数据兼容保留
 */
export type Gender =
  | "男"
  | "女"
  | "未知"
  | "未说明"
  | "其他"
  | "male"
  | "female"
  | "unknown";

/**
 * 患者实体接口（全系统唯一定义）
 */
export interface Patient {
  id: ID;
  patientId?: string;
  name: string;
  gender?: Gender;
  birthDate?: Timestamp;
  age?: number;
  phone?: string;
  email?: string;
  address?: string;
  idCard?: string;
  country?: string;
  province?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  wechatOpenid?: string;
  patientType?: string;
  organization?: string;
  vipLevel?: string;
  status?: PatientStatus;
  notes?: string;
  tags?: string[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * 患者摘要信息（用于列表显示）
 */
export interface PatientSummary {
  id: ID;
  patientId?: string;
  name: string;
  gender?: Gender;
  age?: number;
  phone?: string;
  wechatOpenid?: string;
  status?: PatientStatus;
}

/**
 * 患者简要信息（病历/AI 模块跨域使用）
 */
export interface PatientBrief {
  id: ID;
  name: string;
  gender?: Gender;
  age?: number;
  phone?: string;
}

/**
 * 创建患者参数
 */
export interface CreatePatientParams {
  name: string;
  gender?: Gender;
  birthDate?: Timestamp;
  phone?: string;
  email?: string;
  address?: string;
  idCard?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  notes?: string;
}

/**
 * 更新患者参数
 */
export interface UpdatePatientParams {
  name?: string;
  gender?: Gender;
  birthDate?: Timestamp;
  phone?: string;
  email?: string;
  address?: string;
  idCard?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  wechatOpenid?: string;
  notes?: string;
  status?: PatientStatus;
}

// ==================== 领域逻辑函数 ====================

/**
 * 创建新患者
 */
export function createPatient(
  params: CreatePatientParams,
): Omit<Patient, "id"> {
  return {
    ...params,
    status: PatientStatus.ACTIVE,
    age: params.birthDate ? calculateAge(params.birthDate) : undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 计算年龄
 */
export function calculateAge(birthDate: Timestamp): number {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * 激活患者
 */
export function activatePatient(patient: Patient): Patient {
  if (patient.status === PatientStatus.ACTIVE) {
    throw new Error("患者已处于活跃状态");
  }
  return {
    ...patient,
    status: PatientStatus.ACTIVE,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 停用患者
 */
export function deactivatePatient(patient: Patient): Patient {
  if (patient.status === PatientStatus.INACTIVE) {
    throw new Error("患者已处于非活跃状态");
  }
  return {
    ...patient,
    status: PatientStatus.INACTIVE,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 归档患者
 */
export function archivePatient(patient: Patient): Patient {
  return {
    ...patient,
    status: PatientStatus.ARCHIVED,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 更新联系方式
 */
export function updateContact(
  patient: Patient,
  phone?: string,
  email?: string,
  address?: string,
): Patient {
  return {
    ...patient,
    phone: phone ?? patient.phone,
    email: email ?? patient.email,
    address: address ?? patient.address,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 更新紧急联系人
 */
export function updateEmergencyContact(
  patient: Patient,
  emergencyContact?: string,
  emergencyPhone?: string,
): Patient {
  return {
    ...patient,
    emergencyContact: emergencyContact ?? patient.emergencyContact,
    emergencyPhone: emergencyPhone ?? patient.emergencyPhone,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * 检查患者是否活跃
 */
export function isPatientActive(patient: Patient): boolean {
  return patient.status === PatientStatus.ACTIVE;
}

/**
 * 获取患者显示名称
 */
export function getPatientDisplayName(patient: Patient): string {
  return patient.name || "未知患者";
}

/**
 * 获取患者年龄显示
 */
export function getPatientAgeDisplay(patient: Patient): string {
  if (patient.age !== undefined) {
    return `${patient.age}岁`;
  }
  if (patient.birthDate) {
    return `${calculateAge(patient.birthDate)}岁`;
  }
  return "年龄未知";
}

/**
 * 获取性别显示文本
 */
export function getGenderDisplay(gender?: Gender): string {
  switch (gender) {
    case "male":
    case "男":
      return "男";
    case "female":
    case "女":
      return "女";
    case "未说明":
    case "其他":
      return "未说明";
    default:
      return "未知";
  }
}

/**
 * 手机号脱敏
 */
export function maskPhone(phone?: string): string {
  if (!phone || phone.length < 7) return phone || "";
  return phone.substring(0, 3) + "****" + phone.substring(phone.length - 4);
}

/**
 * 身份证号脱敏
 */
export function maskIdCard(idCard?: string): string {
  if (!idCard || idCard.length < 10) return idCard || "";
  return (
    idCard.substring(0, 6) + "********" + idCard.substring(idCard.length - 4)
  );
}
